import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2 } from "lucide-react";
import { getCheckoutStatus } from "../lib/api";
import { useCart } from "../context/CartContext";

const MAX_POLL = 8;
const INTERVAL = 2000;

const CheckoutSuccess = () => {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { clear } = useCart();
    const [state, setState] = useState({
        status: "checking",
        payment_status: null,
        amount: 0,
    });
    const attempts = useRef(0);
    const clearedRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", payment_status: null, amount: 0 });
            return;
        }
        let timer;
        const poll = async () => {
            try {
                const data = await getCheckoutStatus(sessionId);
                if (data.payment_status === "paid") {
                    if (!clearedRef.current) {
                        clear();
                        clearedRef.current = true;
                    }
                    setState({
                        status: "paid",
                        payment_status: "paid",
                        amount: (data.amount_total || 0) / 100,
                    });
                    return;
                }
                if (data.status === "expired") {
                    setState({
                        status: "expired",
                        payment_status: data.payment_status,
                        amount: 0,
                    });
                    return;
                }
                if (attempts.current >= MAX_POLL) {
                    setState({
                        status: "timeout",
                        payment_status: data.payment_status,
                        amount: 0,
                    });
                    return;
                }
                attempts.current += 1;
                timer = setTimeout(poll, INTERVAL);
            } catch {
                setState({
                    status: "error",
                    payment_status: null,
                    amount: 0,
                });
            }
        };
        poll();
        return () => clearTimeout(timer);
    }, [sessionId, clear]);

    return (
        <div
            data-testid="page-checkout-success"
            className="max-w-2xl mx-auto px-6 py-24 md:py-32 text-center"
        >
            {state.status === "checking" && (
                <div data-testid="checkout-checking">
                    <Loader2
                        className="mx-auto animate-spin text-[#0A2342]"
                        size={32}
                    />
                    <p className="mt-6 text-[11px] tracking-[0.22em] uppercase text-[#5C6A79]">
                        Confirming payment…
                    </p>
                </div>
            )}
            {state.status === "paid" && (
                <div data-testid="checkout-paid">
                    <CheckCircle2
                        className="mx-auto text-[#9E5A35]"
                        size={48}
                        strokeWidth={1.5}
                    />
                    <h1 className="font-serif-display text-4xl md:text-5xl text-[#0A2342] mt-6">
                        Thank you.
                    </h1>
                    <p className="mt-4 text-base text-[#1A1A1A]/80">
                        Your order has been confirmed. A receipt is on its way
                        to your inbox.
                    </p>
                    {state.amount > 0 && (
                        <p className="mt-2 text-sm text-[#5C6A79]">
                            Total charged: ${state.amount.toFixed(2)}
                        </p>
                    )}
                    <div className="mt-10">
                        <Link
                            data-testid="success-continue-shopping"
                            to="/shop"
                            className="btn-primary"
                        >
                            Continue Shopping
                        </Link>
                    </div>
                </div>
            )}
            {(state.status === "expired" ||
                state.status === "timeout" ||
                state.status === "error") && (
                <div data-testid="checkout-failed">
                    <h1 className="font-serif-display text-4xl text-[#0A2342]">
                        Something went sideways.
                    </h1>
                    <p className="mt-4 text-base text-[#1A1A1A]/80">
                        {state.status === "expired"
                            ? "Your session expired. Please try again."
                            : "We couldn't confirm your payment. If you were charged, please contact us."}
                    </p>
                    <div className="mt-10 flex gap-4 justify-center">
                        <Link to="/shop" className="btn-primary">
                            Back to Shop
                        </Link>
                        <Link to="/contact" className="btn-outline">
                            Contact Us
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CheckoutSuccess;
